import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useChangeEmailMutation, useLogoutMutation, useProfileQuery, useResendCodeMutation, useUpdateProfileMutation, useVerifyEmailMutation } from "../features/auth/authApi";
import Modal from "./Modal";
import ForgotPassword from "./ForgotPassword";

const ClientProfile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const [tab, setTab] = useState("details");
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [phone, setPhone] = useState("");
  const [savedPhone, setSavedPhone] = useState("");
  const [profileMessage, setProfileMessage] = useState("");
  const [profileMessageStatus, setProfileMessageStatus] = useState(null);
  const [profileErrors, setProfileErrors] = useState({});
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState(1);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailResendSeconds, setEmailResendSeconds] = useState(0);
  const [changeEmail, { isLoading: isChangingEmail }] = useChangeEmailMutation();
  const [resendCode, { isLoading: isResendingEmailCode }] = useResendCodeMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation();
  const [verifyEmail, { isLoading: isVerifyingEmail }] = useVerifyEmailMutation();
  const { data: profileResponse, refetch: refetchProfile } = useProfileQuery();
  const user = profileResponse?.data?.user || profileResponse?.user || profileResponse?.data || {};
  const email = user.email || "";

  const updateEmailCode = (index, value) => {
    const digit = value.replace(/[^a-zA-Z0-9]/g, "").slice(-1).toUpperCase();
    const nextCode = emailCode.split("");
    nextCode[index] = digit;
    setEmailCode(nextCode.join("").slice(0, 4));
    if (digit) document.getElementById(`email-change-code-${index + 1}`)?.focus();
  };

  const getProfileError = (field) => {
    const value = profileErrors[field];
    return Array.isArray(value) ? value.join(" ") : typeof value === "string" ? value : "";
  };

  const clearProfileError = (field) => {
    if (!profileErrors[field]) return;
    setProfileErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  useEffect(() => {
    setName(user.name || user.owner_name || "");
    setLogin(user.login || user.username || "");
    setPhone(user.owner_phone || user.phone || "");
    setSavedPhone(user.owner_phone || user.phone || "");
  }, [user.name, user.owner_name, user.owner_phone, user.phone]);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch {
      // The local session is cleared by the logout mutation even if the API is unavailable.
    } finally {
      navigate(i18n.language === "hy" ? "/" : `/${i18n.language}`);
    }
  };

  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    setProfileMessage("");
    setProfileMessageStatus(null);
    setProfileErrors({});
    try {
      await updateProfile({
        phone: phone.trim(),
        name: name.trim(),
        login: login.trim(),
        email,
      }).unwrap();
      setSavedPhone(phone.trim());
      setProfileMessage("Անձնական տվյալները հաջողությամբ պահպանվեցին։");
      setProfileMessageStatus("success");
    } catch (error) {
      if (error?.data?.errors && typeof error.data.errors === "object") {
        setProfileErrors(error.data.errors);
      }
      setProfileMessage(error?.data?.message || error?.data?.error || "Չհաջողվեց պահպանել տվյալները։");
      setProfileMessageStatus("error");
      if (error?.data?.errors && typeof error.data.errors === "object") {
        setProfileMessage("");
      }
    }
  };

  useEffect(() => {
    document.querySelectorAll(".client-profile-email-account input").forEach((input) => {
      input.setAttribute("autocomplete", input.type === "password" ? "new-password" : "off");
    });
  }, [emailModalOpen, emailStep]);

  useEffect(() => {
    if (!emailResendSeconds) return undefined;
    const timer = setInterval(() => setEmailResendSeconds((seconds) => seconds - 1), 1000);
    return () => clearInterval(timer);
  }, [emailResendSeconds]);

  const handleEmailResend = async () => {
    await resendCode({ email: newEmail.trim(), purpose: "email_change" }).unwrap();
    setEmailResendSeconds(60);
    setEmailCode("");
  };

  const handleChangeEmail = async (event) => {
    event.preventDefault();
    setEmailMessage("");
    try {
      if (emailStep === 1) {
        await changeEmail({ email: newEmail.trim(), current_password: currentPassword }).unwrap();
      } else {
        await verifyEmail({ email: newEmail.trim(), code: emailCode.trim().toUpperCase(), purpose: "email_change" }).unwrap();
      }
      if (emailStep === 1) {
        setEmailStep(2);
        setEmailResendSeconds(60);
        setEmailMessage("Հաստատման կոդը ուղարկվել է ձեր նոր էլ․ փոստին։");
      } else {
        await refetchProfile();
        setEmailMessage("Էլ․ փոստը հաջողությամբ փոխվեց։");
        setTimeout(() => setEmailModalOpen(false), 700);
      }
    } catch (error) {
      setEmailMessage(error?.data?.message || error?.data?.error || "Չհաջողվեց փոխել էլ․ փոստը։");
    }
  };

  return (
    <main className="client-profile-page">
      <div className="client-profile-cover" />
      <div className="client-profile-layout">
        <aside className="client-profile-sidebar">
          <div className="client-profile-avatar">GA</div>
          <h3>{user.name || user.owner_name || "Gap Avoyan"}</h3>
          <p>{t("profilePage.client", "Բարի վերադարձ")}</p>
          <div className="client-profile-contact"><strong><i className="las la-user" />{t("profilePage.fullName", "Անուն Ազգանուն")}</strong><span>{user.name || user.owner_name || "—"}</span></div>
          <div className="client-profile-contact"><strong><i className="las la-envelope" />{t("profilePage.email", "Էլ․ փոստ")}</strong><span>{email || "—"}</span></div>
          <div className="client-profile-contact"><strong><i className="las la-phone" />{t("profilePage.phone", "Հեռախոս")}</strong><span>{savedPhone || "—"}</span></div>
          <button className="client-profile-home" onClick={() => navigate(i18n.language === "hy" ? "/" : `/${i18n.language}`)}>{t("profilePage.home", "Գլխավոր էջ")}</button>
          <button className="client-profile-logout" onClick={handleLogout}>{t("profilePage.exit", "Ելք")}</button>
        </aside>
        <section className="client-profile-content">
          <nav className="client-profile-tabs">
            <button className={tab === "details" ? "active" : ""} onClick={() => setTab("details")}>{t("profilePage.details", "Անձնական տվյալներ")}</button>
            <button className={tab === "security" ? "active" : ""} onClick={() => setTab("security")}>{t("profilePage.security", "Անվտանգություն")}</button>
            <button className={tab === "email" ? "active" : ""} onClick={() => setTab("email")}>Էլ․ փոստ</button>
          </nav>
          {tab === "details" ? <>
            <h2>{t("profilePage.details", "Անձնական տվյալներ")}</h2>
            <p className="client-profile-description">{t("profilePage.description", "Թարմացրեք ձեր անունը և հեռախոսահամարը։")}</p>
            <form className="client-profile-form" onSubmit={handleProfileUpdate}>
              <label>{t("profilePage.fullName", "Անուն Ազգանուն")}<input value={name} onChange={(e) => { setName(e.target.value); clearProfileError("name"); }} placeholder="Gap Avoyan" />{getProfileError("name") && <span className="client-profile-field-error">{getProfileError("name")}</span>}</label>
              <label>Մուտքանուն<input value={login} onChange={(e) => { setLogin(e.target.value); clearProfileError("login"); }} placeholder="Մուտքանուն" autoComplete="username" />{getProfileError("login") && <span className="client-profile-field-error">{getProfileError("login")}</span>}</label>
              <label>{t("profilePage.phone", "Հեռախոս")}<input value={phone} onChange={(e) => { setPhone(e.target.value); clearProfileError("phone"); }} placeholder="+374..." />{getProfileError("phone") && <span className="client-profile-field-error">{getProfileError("phone")}</span>}</label>
              {profileMessage && <p className={`client-profile-profile-message client-profile-form-message ${profileMessageStatus === "error" ? "text-danger" : "text-success"}`}>{profileMessage}</p>}
              <button type="submit" className="client-profile-save" disabled={isUpdatingProfile}>{isUpdatingProfile ? "..." : t("profilePage.save", "Պահպանել")}</button>
            </form>
          </> : tab === "security" ? <div className="client-profile-security-panel">
            <h2>{t("profilePage.security", "Անվտանգություն")}</h2>
            <p className="client-profile-description">Կառավարեք ինչպես եք մուտք գործում ձեր հաշիվ։</p>
            <div className="client-profile-security-card">
              <span className="client-profile-security-icon"><i className="las la-key" /></span>
              <div>
                <strong>Գաղտնաբառ</strong>
                <p>Վերականգնեք գաղտնաբառը, եթե մոռացել եք։</p>
              </div>
              <button className="client-profile-outline-button" onClick={() => setPasswordResetOpen(true)}>Վերականգնել գաղտնաբառը</button>
            </div>
          </div> : null}
          {tab === "email" && <div className="client-profile-email-panel"><h2>Էլ․ փոստ</h2><p className="client-profile-description">Սա ձեր մուտքի և ծանուցումների համար օգտագործվող էլ․ փոստն է։ Կարող եք ստորև փոխել։</p><label className="client-profile-current-email"><span>Ընթացիկ էլ․ փոստ</span><input value={email || "—"} readOnly /></label><button className="client-profile-save" onClick={() => setEmailModalOpen(true)}>Փոխել էլ․ փոստը</button></div>}
        </section>
      </div>
      <Modal isOpen={emailModalOpen} onClose={() => { setEmailModalOpen(false); setEmailStep(1); }} withoutLogo>
        <section className="account d-flex client-profile-email-account">
          <div className="account__right padding-y-120 flx-align">
            <div className="account-content">
              <Link to="/" className="logo"><img src="assets/images/logo/logo.png" alt="" className="white-version" /><img src="assets/images/logo/white-logo.png" alt="" className="dark-version" /></Link>
              <h4 className="account-content__title text-capitalize">Փոխել էլ․ փոստը</h4>
              <p>{emailStep === 1 ? "Մուտքագրեք նոր էլ․ փոստը, որը ցանկանում եք օգտագործել։ Մենք կուղարկենք հաստատման կոդ։" : "Մուտքագրեք ձեր էլ․ փոստին ուղարկված հաստատման կոդը։"}</p>
              {emailStep === 1 && <p className="client-profile-description">Ընթացիկ էլ․ փոստ: {email}</p>}
              <form onSubmit={handleChangeEmail} className="row" autoComplete="new-password">
                <div className="col-12">
                  <label className="form-label font-18 font-heading fw-600">{emailStep === 1 ? "Նոր էլ․ փոստ" : "Հաստատման կոդ"}</label>
                  {emailStep === 1 && <input className="common-input common-input--bg" type="password" placeholder="Ընթացիկ գաղտնաբառ" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required />}
                  {emailStep === 1 ? <input className="common-input common-input--bg" type="email" placeholder="new.email@example.com" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} required /> : <div className="verify-code-inputs">
                    {[0, 1, 2, 3].map((index) => <input key={index} id={`email-change-code-${index}`} className="common-input common-input--bg" type="text" inputMode="text" maxLength={1} value={emailCode[index] || ""} onChange={(event) => updateEmailCode(index, event.target.value)} autoComplete={index === 0 ? "one-time-code" : "off"} required />)}
                  </div>}
                </div>
                {emailMessage && <div className="col-12 text-success">{emailMessage}</div>}
                <div className="col-12"><button className="btn btn-main btn-lg w-100 pill" type="submit" disabled={isChangingEmail}>{isChangingEmail ? "..." : emailStep === 1 ? "Ուղարկել կոդը" : "Հաստատել կոդը"}</button></div>
                {emailStep === 2 && <div className="col-12 verify-resend">{emailResendSeconds > 0 ? `Կրկին ուղարկել ${emailResendSeconds} վրկ․ հետո` : <button type="button" onClick={handleEmailResend} disabled={isResendingEmailCode}>{isResendingEmailCode ? "..." : "Կրկին ուղարկել"}</button>}</div>}
              </form>
            </div>
          </div>
        </section>
      </Modal>
      <Modal isOpen={passwordResetOpen} onClose={() => setPasswordResetOpen(false)} withoutLogo>
        <ForgotPassword
          setOpenForgotPasswordModal={setPasswordResetOpen}
          resetMode
          setOpenLoginModal={(open) => {
            if (open) handleLogout();
          }}
        />
      </Modal>
    </main>
  );
};

export default ClientProfile;
